import { API_BASE_URL } from "@/lib/api/constants";
import { getPendingRecordsSnapshot, removePendingRecord, PendingRecord, writePendingRecords } from "./pendingRecords";
import { processOfflineQueue } from "./queue";
import { jsonToFormData } from "./pendingRecords";

const isBrowser = typeof window !== "undefined";
let isSyncing = false;
const syncingRecordIds = new Set<string>();

/**
 * Sync a single pending record to the server
 */
const syncPendingRecord = async (record: PendingRecord): Promise<boolean> => {
  if (!isBrowser || !navigator.onLine) {
    return false;
  }

  // Prevent duplicate syncs of the same record
  if (syncingRecordIds.has(record.id)) {
    console.log(`[sync] Record ${record.id} is already being synced, skipping`);
    return false;
  }

  syncingRecordIds.add(record.id);

  try {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {
      ...(record.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    let body: BodyInit | undefined;

    // Handle FormData for goat creation with images
    if (record.imageData && record.type === "goat") {
      const formData = jsonToFormData(record.data, record.imageData);
      
      // Verify the image file was added to FormData
      const imageFile = formData.get("image");
      if (!imageFile || !(imageFile instanceof File)) {
        console.error(`[sync] Failed to convert image data to File for record ${record.id}`);
        throw new Error("Failed to convert image data to File for sync");
      }
      
      const file = imageFile as File;
      console.log(`[sync] Syncing goat record ${record.id} with image:`, {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      });
      
      // Log FormData contents for debugging
      console.log(`[sync] FormData entries:`, Array.from(formData.entries()).map(([key, value]) => {
        if (value instanceof File) {
          return [key, `File: ${value.name} (${value.size} bytes, ${value.type})`];
        }
        return [key, String(value)];
      }));
      
      body = formData;
      // Don't set Content-Type for FormData, browser will set it with boundary
      delete headers["Content-Type"];
    } else if (record.body) {
      body = record.body;
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${API_BASE_URL}${record.endpoint}`, {
      method: record.method,
      headers,
      body,
    });

    // Parse response (can only be read once)
    let responseData: any;
    let responseText: string = "";
    
    try {
      responseText = await response.text();
      if (responseText) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          // Not JSON, treat as plain text
          responseData = { success: response.ok, message: responseText };
        }
      } else {
        responseData = { success: response.ok };
      }
    } catch (error) {
      // If reading fails, check status
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      responseData = { success: true };
    }

    // Log response for debugging (especially for goat records with images)
    if (record.type === "goat" && record.imageData) {
      console.log(`[sync] Response status: ${response.status}, Response data:`, {
        success: responseData.success,
        hasData: !!responseData.data,
        imageUrl: responseData.data?.imageUrl,
        imagePublicId: responseData.data?.imagePublicId,
        message: responseData.message,
      });
    }

    // Check if request was successful
    if (!response.ok) {
      console.error(`[sync] Request failed with status ${response.status}:`, responseData);
      throw new Error(responseData.message || responseText || `Request failed with status ${response.status}`);
    }

    // Only remove if response indicates success
    if (responseData.success !== false) {
      // Log the response data for debugging, especially for goat records with images
      if (record.type === "goat" && responseData.data) {
        const goatData = responseData.data;
        console.log(`[sync] Goat synced successfully:`, {
          id: goatData._id,
          tagId: goatData.tagId,
          goatNumber: goatData.goatNumber,
          imageUrl: goatData.imageUrl,
          imagePublicId: goatData.imagePublicId,
          hasImage: !!goatData.imageUrl,
        });
        
        // Warn if image was expected but not in response
        if (record.imageData && !goatData.imageUrl) {
          console.warn(`[sync] WARNING: Image was sent but imageUrl is missing in response for goat ${goatData._id || record.id}`);
        }
      }
      
      // Successfully synced, remove from pending records IMMEDIATELY
      const removed = removePendingRecord(record.id);
      syncingRecordIds.delete(record.id);
      
      if (removed) {
        console.log(`[sync] Successfully synced and removed pending record ${record.id}`);
        return true;
      } else {
        console.warn(`[sync] Record ${record.id} synced but removal failed (may have been already removed)`);
        // Still return true since sync was successful
        return true;
      }
    } else {
      throw new Error(responseData.message || "Request failed");
    }
  } catch (error) {
    console.warn(`[sync] Failed to sync pending record ${record.id}:`, error);
    
    // Update the pending record with retry information instead of removing it
    const errorMessage = error instanceof Error ? error.message : String(error);
    updatePendingRecordRetry(record.id, errorMessage);
    
    syncingRecordIds.delete(record.id);
    return false;
  }
};

/**
 * Update retry information for a pending record that failed to sync
 */
const updatePendingRecordRetry = (recordId: string, errorMessage: string): void => {
  if (!isBrowser) return;
  
  try {
    const records = getPendingRecordsSnapshot(true);
    const recordIndex = records.findIndex((r) => r.id === recordId);
    
    if (recordIndex !== -1) {
      const record = records[recordIndex];
      record.retryCount = (record.retryCount || 0) + 1;
      record.lastRetryAt = Date.now();
      record.lastError = errorMessage;
      
      // Write back to storage
      writePendingRecords(records);
      
      const maxRetries = 5; // Maximum number of retry attempts
      if (record.retryCount >= maxRetries) {
        console.error(`[sync] Record ${recordId} has exceeded max retries (${maxRetries}). Keeping in pending for manual review.`);
      } else {
        console.log(`[sync] Updated retry count for record ${recordId}: ${record.retryCount}/${maxRetries}`);
      }
    }
  } catch (error) {
    console.error(`[sync] Failed to update retry info for record ${recordId}:`, error);
  }
};

/**
 * Check if a record should be retried based on retry count and backoff timing
 */
const shouldRetryRecord = (record: PendingRecord): boolean => {
  const maxRetries = 5;
  const retryCount = record.retryCount || 0;
  
  // Don't retry if exceeded max attempts
  if (retryCount >= maxRetries) {
    return false;
  }
  
  // If never retried, always retry
  if (retryCount === 0 || !record.lastRetryAt) {
    return true;
  }
  
  // Exponential backoff: wait 2^retryCount seconds (2s, 4s, 8s, 16s, 32s)
  const backoffSeconds = Math.pow(2, retryCount);
  const backoffMs = backoffSeconds * 1000;
  const timeSinceLastRetry = Date.now() - record.lastRetryAt;
  
  // Only retry if enough time has passed since last retry
  return timeSinceLastRetry >= backoffMs;
};

/**
 * Sync all pending records to the server
 */
export const syncPendingRecords = async (): Promise<{ synced: number; failed: number }> => {
  if (!isBrowser || !navigator.onLine) {
    return { synced: 0, failed: 0 };
  }

  // Prevent concurrent syncs
  if (isSyncing) {
    console.log("[sync] Sync already in progress, skipping");
    return { synced: 0, failed: 0 };
  }

  isSyncing = true;

  try {
    // Get fresh snapshot each time to avoid stale data
    const pendingRecords = getPendingRecordsSnapshot(true); // Force refresh
    if (pendingRecords.length === 0) {
      return { synced: 0, failed: 0 };
    }

    let synced = 0;
    let failed = 0;
    const syncedTypes = new Set<string>();

    // Sync records one by one to avoid overwhelming the server
    // Re-read snapshot before each sync to get latest state
    for (let i = 0; i < pendingRecords.length; i++) {
      // Get fresh snapshot to ensure we have the latest records (force refresh)
      const currentRecords = getPendingRecordsSnapshot(true);
      const record = currentRecords.find((r) => r.id === pendingRecords[i].id);
      
      // Skip if record was already removed by another sync
      if (!record) {
        console.log(`[sync] Record ${pendingRecords[i].id} was already removed, skipping`);
        continue;
      }

      // Check if record should be retried (respects backoff timing)
      if (!shouldRetryRecord(record)) {
        const retryCount = record.retryCount || 0;
        if (retryCount >= 5) {
          console.warn(`[sync] Record ${record.id} exceeded max retries, skipping`);
        } else {
          const backoffSeconds = Math.pow(2, retryCount);
          const timeSinceLastRetry = Date.now() - (record.lastRetryAt || 0);
          const waitTime = (backoffSeconds * 1000 - timeSinceLastRetry) / 1000;
          console.log(`[sync] Record ${record.id} in backoff period, will retry in ${Math.ceil(waitTime)}s`);
        }
        failed++;
        continue;
      }

      const success = await syncPendingRecord(record);
      if (success) {
        synced++;
        syncedTypes.add(record.type);
      } else {
        failed++;
      }
      // Small delay between requests to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Also process the offline queue (for other requests that weren't tracked as records)
    await processOfflineQueue();

    // Dispatch event with synced types if any records were synced
    if (synced > 0) {
      window.dispatchEvent(
        new CustomEvent("alraheem:records-synced", {
          detail: { synced, failed, types: Array.from(syncedTypes) },
        })
      );
    }

    return { synced, failed };
  } finally {
    isSyncing = false;
    syncingRecordIds.clear();
  }
};

let syncHandler: (() => void) | null = null;
let isSetup = false;

/**
 * Set up automatic sync when coming back online
 */
export const setupAutoSync = () => {
  if (!isBrowser) {
    return () => void 0;
  }

  // Prevent multiple setups
  if (isSetup && syncHandler) {
    return () => {
      if (syncHandler) {
        window.removeEventListener("online", syncHandler);
      }
    };
  }

  isSetup = true;

  syncHandler = () => {
    // Wait a bit for network to stabilize
    setTimeout(() => {
      syncPendingRecords().then(({ synced, failed }) => {
        if (synced > 0) {
          console.log(`[sync] Synced ${synced} pending record(s)`);
          // Event is dispatched inside syncPendingRecords with types
        }
      });
    }, 1000);
  };

  window.addEventListener("online", syncHandler);

  // Also try to sync immediately if already online
  if (navigator.onLine) {
    syncHandler();
  }

  return () => {
    if (syncHandler) {
      window.removeEventListener("online", syncHandler);
      syncHandler = null;
      isSetup = false;
    }
  };
};
